import { loadTokens } from "./auth-client";

/**
 * Base URL for the BFF (`apps/api` worker). Local dev: `localhost:8580`
 * (pahfacturar 8580-8589 range; see docs/LOCAL_DEV.md);
 * production: `https://api.pahfacturar.com`.
 */
const BASE =
  import.meta.env.VITE_API_BASE_URL ??
  (import.meta.env.DEV
    ? "http://localhost:8580"
    : "https://api.pahfacturar.com");

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
};

/**
 * Module-scoped getter for the active team id. The `TeamProvider` overwrites
 * this on mount so subsequent `fetchApiWithAuth` calls auto-inject the
 * `X-Team-Id` header on company- and DIAN-scoped endpoints. Avoids a circular
 * import (lib/api.ts is imported by TeamProvider's downstream callers).
 */
let getActiveTeamId: () => string | null = () => null;

export function setActiveTeamIdGetter(getter: () => string | null) {
  getActiveTeamId = getter;
}

/**
 * Module-scoped getter for the active company id. The `CompanyProvider`
 * overwrites this on mount so subsequent `fetchApiWithAuth` calls auto-inject
 * the `X-Company-Id` header on /api/me/dian/* requests (the company-scoped
 * BFF surface). Same circular-import workaround as `setActiveTeamIdGetter`.
 */
let getActiveCompanyId: () => string | null = () => null;

export function setActiveCompanyIdGetter(getter: () => string | null) {
  getActiveCompanyId = getter;
}

/**
 * Module-scoped 401 handler. The AuthProvider wires this on mount to bounce
 * the user back through OpenAuth's PKCE flow when the access token expires
 * mid-session. Without this, every authenticated query just returns isError
 * and pages render the generic "connection error" card forever — the user
 * has no idea their token expired.
 *
 * Once fired, `unauthorizedHandled` stays true for the rest of this page
 * lifetime — we are about to navigate away via signIn(), and any further
 * 401s in the meantime are noise that must NOT trigger another redirect
 * (which would race with the one already in flight and could ping-pong
 * between /authorize and /auth/callback). The flag resets only on the next
 * full page load.
 */
let onUnauthorized: (() => void) | null = null;
let unauthorizedHandled = false;

export function setOnUnauthorized(handler: (() => void) | null) {
  onUnauthorized = handler;
}

/**
 * Paths that require the `X-Team-Id` header for tenant scoping. The team
 * routes themselves carry teamId in the URL so they don't need it; only the
 * company- and DIAN-scoped endpoints rely on the header.
 */
function pathNeedsTeamHeader(path: string): boolean {
  return (
    path.startsWith("/api/me/companies") ||
    path.startsWith("/api/me/dian") ||
    path.startsWith("/api/me/subscriptions")
  );
}

/**
 * Paths that require the `X-Company-Id` header. Every per-company sub-route
 * under `/api/me/companies/:companyId/*` runs `requireCompany`, which reads
 * the active company from `x-company-id`. The companies list/create endpoint
 * (`/api/me/companies` exact, no `:companyId` segment) doesn't need it, but
 * injecting an extra header there is harmless. `/api/me/dian` also uses
 * `requireCompany`.
 *
 * Wrappers MAY still pass the header explicitly; the auto-injector only fills
 * it in when missing, so explicit overrides win.
 */
function pathNeedsCompanyHeader(path: string): boolean {
  return (
    path.startsWith("/api/me/companies/") || path.startsWith("/api/me/dian")
  );
}

async function request<T>(
  path: string,
  options: RequestOptions,
  withAuth: boolean,
): Promise<T> {
  const headers = new Headers(options.headers);
  if (withAuth) {
    const tokens = loadTokens();
    if (tokens?.access) headers.set("Authorization", `Bearer ${tokens.access}`);
    if (pathNeedsTeamHeader(path) && !headers.has("X-Team-Id")) {
      const teamId = getActiveTeamId();
      if (teamId) headers.set("X-Team-Id", teamId);
    }
    if (pathNeedsCompanyHeader(path) && !headers.has("X-Company-Id")) {
      const pathCompany = /^\/api\/me\/companies\/([^/?]+)\//.exec(path)?.[1];
      const companyId = pathCompany
        ? decodeURIComponent(pathCompany)
        : getActiveCompanyId();
      if (companyId) headers.set("X-Company-Id", companyId);
    }
  }
  if (options.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!headers.has("Accept")) headers.set("Accept", "application/json");

  const url = path.startsWith("http") ? path : `${BASE}${path}`;
  const { body: rawBody, ...rest } = options;
  const init: RequestInit = { ...rest, headers };
  if (rawBody !== undefined) {
    init.body = typeof rawBody === "string" ? rawBody : JSON.stringify(rawBody);
  }

  const res = await fetch(url, init);
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const errBody = parsed as {
      error?: { code?: string; message?: string; details?: unknown };
    } | null;
    if (
      res.status === 401 &&
      withAuth &&
      onUnauthorized &&
      !unauthorizedHandled
    ) {
      // One-shot: never re-fire on this page. signIn() does an async
      // discovery-doc fetch before redirecting, and any 401 that lands
      // during that window MUST NOT trigger another redirect — that's the
      // /authorize ↔ /auth/callback ping-pong this whole guard exists to
      // prevent. The flag resets on the next full page load.
      unauthorizedHandled = true;
      onUnauthorized();
    }
    throw new ApiError(
      res.status,
      errBody?.error?.message ?? `HTTP ${res.status}`,
      errBody?.error?.code,
      errBody?.error?.details,
    );
  }

  return parsed as T;
}

/** Authenticated fetch — pulls the current Bearer token from localStorage. */
export function fetchApiWithAuth<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return request<T>(path, options, true);
}

/**
 * Unauthenticated fetch for the `/api/public/*` surfaces (PF-345 invitation
 * token links) — same JSON/error handling as `fetchApiWithAuth`, but no
 * Authorization header and (since `withAuth` is false) no one-shot 401
 * redirect: a public page must never bounce the visitor into OAuth.
 */
export function fetchApiPublic<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return request<T>(path, options, false);
}

/**
 * Authenticated fetch for binary endpoints (PDF previews, artifact bytes) —
 * same token / X-Team-Id / X-Company-Id injection and the same one-shot 401
 * `onUnauthorized` behavior as `fetchApiWithAuth`, but resolves the raw
 * response `Blob` instead of parsing JSON. Non-2xx responses parse the JSON
 * error envelope (when present) and throw the same `ApiError` shape.
 */
export async function fetchApiBlobWithAuth(
  path: string,
  options: Omit<RequestInit, "body"> = {},
): Promise<Blob> {
  const headers = new Headers(options.headers);
  const tokens = loadTokens();
  if (tokens?.access) headers.set("Authorization", `Bearer ${tokens.access}`);
  if (pathNeedsTeamHeader(path) && !headers.has("X-Team-Id")) {
    const teamId = getActiveTeamId();
    if (teamId) headers.set("X-Team-Id", teamId);
  }
  if (pathNeedsCompanyHeader(path) && !headers.has("X-Company-Id")) {
    const pathCompany = /^\/api\/me\/companies\/([^/?]+)\//.exec(path)?.[1];
    const companyId = pathCompany
      ? decodeURIComponent(pathCompany)
      : getActiveCompanyId();
    if (companyId) headers.set("X-Company-Id", companyId);
  }

  const url = path.startsWith("http") ? path : `${BASE}${path}`;
  const res = await fetch(url, { ...options, headers });

  if (!res.ok) {
    type ErrorEnvelope = {
      error?: { code?: string; message?: string; details?: unknown };
    };
    let errBody: ErrorEnvelope | null = null;
    try {
      errBody = (await res.json()) as ErrorEnvelope;
    } catch {
      errBody = null;
    }
    if (res.status === 401 && onUnauthorized && !unauthorizedHandled) {
      // Same one-shot guard as `request` — see setOnUnauthorized above.
      unauthorizedHandled = true;
      onUnauthorized();
    }
    throw new ApiError(
      res.status,
      errBody?.error?.message ?? `HTTP ${res.status}`,
      errBody?.error?.code,
      errBody?.error?.details,
    );
  }

  return res.blob();
}
